/**
 * Standardized error response from API calls
 */
export interface ApiError {
  message: string
  code?: string | number
  details?: Record<string, any>
}

/**
 * Extract and standardize error messages from API responses
 * Handles both Axios errors and generic errors
 *
 * @param error - The error object from API call
 * @param fallbackMessage - Default message if error cannot be parsed
 * @returns Standardized ApiError object
 */
export const parseApiError = (error: any, fallbackMessage: string = 'An error occurred'): ApiError => {
  // Check if it's an Axios error with response data
  if (error?.response?.data) {
    const data = error.response.data

    // Handle case where API returns message field
    if (typeof data.message === 'string') {
      return {
        message: data.message,
        code: error.response.status,
        details: data.errors || data.data,
      }
    }

    // Handle case where API returns error field
    if (typeof data.error === 'string') {
      return {
        message: data.error,
        code: error.response.status,
        details: data.data,
      }
    }

    // Handle validation errors (array of error messages)
    if (Array.isArray(data.errors) && data.errors.length > 0) {
      const errorMessages = data.errors
        .map((err: any) => (typeof err === 'string' ? err : err.message || JSON.stringify(err)))
        .join('; ')
      return {
        message: errorMessages,
        code: error.response.status,
        details: data.errors,
      }
    }

    // Fallback to generic response data
    if (typeof data === 'string') {
      return {
        message: data,
        code: error.response.status,
      }
    }
  }

  // Check for network errors
  if (error?.message === 'Network Error') {
    return {
      message: 'Network error. Please check your connection.',
      code: 'NETWORK_ERROR',
    }
  }

  // Check for timeout errors
  if (error?.code === 'ECONNABORTED' || error?.message?.includes('timeout')) {
    return {
      message: 'Request timeout. Please try again.',
      code: 'TIMEOUT',
    }
  }

  // Generic Axios error with message
  if (error?.message) {
    return {
      message: error.message,
      code: error.code,
    }
  }

  // Fallback
  return {
    message: fallbackMessage,
    code: 'UNKNOWN_ERROR',
  }
}

/**
 * Extract field-level validation errors from API response
 * Useful for forms that need to display per-field errors
 *
 * @param error - The error object from API call
 * @returns Record of field names to error messages
 */
export const parseFieldErrors = (error: any): Record<string, string> => {
  const fieldErrors: Record<string, string> = {}

  if (error?.response?.data?.errors) {
    const errors = error.response.data.errors

    if (typeof errors === 'object' && !Array.isArray(errors)) {
      // Handle object format: { field: 'error message' }
      Object.entries(errors).forEach(([field, message]) => {
        fieldErrors[field] = typeof message === 'string' ? message : JSON.stringify(message)
      })
    } else if (Array.isArray(errors)) {
      // Handle array format with field property
      errors.forEach((err: any) => {
        if (err.field && err.message) {
          fieldErrors[err.field] = err.message
        }
      })
    }
  }

  return fieldErrors
}

/**
 * Log error to console with consistent formatting
 *
 * @param context - What operation was being performed
 * @param error - The error object
 */
export const logError = (context: string, error: any): void => {
  const apiError = parseApiError(error)
  console.error(`❌ ${context}:`, {
    message: apiError.message,
    code: apiError.code,
    details: apiError.details,
  })
}
